import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import * as kestrelEmbedCode from "shared/kestrel-embed-code";
import * as yup from "yup";
import { validate } from "shared/yup";
import { getMarketsOptimized } from "redux/selector";
import { WidgetConfigLayout } from "../widget-config-layout";
import { WidgetFieldGrid, WidgetFieldCell, WidgetSelect } from "../widget-fields";
import { getUser } from "redux/selector";
import { MarketReportType } from "type/market-report-type";

const MarketReportConfig: React.FC = () => {
	// Redux selectors
	const user = useSelector(getUser);
	const markets = useSelector(getMarketsOptimized);

	// Local state
	const [id, setId] = useState<number>(0);
	const [marketReportTypeId, setMarketReportTypeId] = useState<number>(0);

	// Memoized validation schema
	const validationSchema = useMemo(() => ({
		id: yup
			.number().moreThan(0)
			.required("Market is required."),
		marketReportTypeId: yup
			.number().test("Market Report is required", (marketReportTypeId) => {
				const market = markets.find((market) => market.id === id);
				return market && market.marketReportTypes.length ? !!marketReportTypeId : true;
			}),
	}), [markets, id]);

	// Memoized selected market
	const selectedMarket = useMemo(() => {
		return markets.find((market) => market.id === id);
	}, [markets, id]);

	// Memoized filter function for market reports
	const filterMarketReports = useCallback((reportType: MarketReportType) => {
		if ('listingReport' === reportType.value && user.permissions.listingReport 
				|| 'marketReport' === reportType.value && user.permissions.marketReport
				|| 'openHomeReport' === reportType.value && user.permissions.openHomeReport) {
					return true;
		}
		return false;
	}, [user.permissions]);

	// Memoized state for validation
	const validationState = useMemo(() => ({ id, marketReportTypeId }), [id, marketReportTypeId]);
	
	// Memoized validation errors
	const errors = useMemo(() => validate(validationSchema, validationState), [validationSchema, validationState]);
	
	// Memoized embed code
	const embedCode = useMemo(() => {
		return kestrelEmbedCode.marketReportWidget(
			user.websitePlatform,
			id,
			marketReportTypeId || MarketReportType.LISTING.id,
		);
	}, [user.websitePlatform, id, marketReportTypeId]);

	// Memoized market report types
	const marketReportTypes = useMemo(() => {
		return selectedMarket?.marketReportTypes.filter((reportType) => filterMarketReports(reportType));
	}, [selectedMarket, filterMarketReports]);

	return (
		<WidgetConfigLayout user={user} embedCode={!errors ? embedCode : undefined}>
			<WidgetFieldGrid>
				<WidgetFieldCell full>
					<WidgetSelect
						label="Market"
						required
						value={id || ""}
						options={markets.map((market) => ({value: market.id, label: market.name}))}
						onChange={(value) => setId(parseInt(value))}
					/>
				</WidgetFieldCell>
				{marketReportTypes && marketReportTypes.length > 0 && (
					<WidgetFieldCell full>
						<WidgetSelect
							label="Type"
							required
							value={marketReportTypeId || ""}
							options={marketReportTypes.map((type) => ({value: type.id, label: type.label}))}
							onChange={(value) => setMarketReportTypeId(parseInt(value))}
						/>
					</WidgetFieldCell>
				)}
			</WidgetFieldGrid>
		</WidgetConfigLayout>			
	);
};

export { MarketReportConfig };